import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase.js'
import { useCurrentUnit } from './useCurrentUnit.js'
import {
  isPushSupported,
  isIosNeedsInstall,
  subscribeToPush,
  unsubscribeFromPush,
  getSubscriptionEndpoint,
  PushPermissionError,
  PushConfigError,
} from '../lib/push.js'

// usePushSubscription — operator-only web push opt-in state machine.
//
// State:
//   unsupported       — browser lacks the push stack; no toggle.
//   ios-needs-install — iOS Safari tab, not the installed PWA.
//   idle              — supported, not yet subscribed.
//   prompting         — mid permission/subscribe.
//   denied            — user blocked notifications (friendly, no throw).
//   subscribed        — active subscription persisted.
//   error             — unexpected failure (friendly message).
//
// `busy` gates the toggle button while a subscribe/unsubscribe is in
// flight. It is ALWAYS reset in `finally` (CLAUDE.md modal rule analog) so
// a success path can never leave the control stuck.
//
// All hooks are declared unconditionally at the top — there are no early
// returns in this hook — so hook order is stable across renders.
export function usePushSubscription() {
  const { currentRestaurantId } = useCurrentUnit()

  const [state, setState] = useState('idle')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')

  // Resolve the initial state once on mount: unsupported / iOS-needs-install
  // take priority, then reflect any already-granted+subscribed browser
  // state so a returning operator sees the toggle "on".
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!isPushSupported()) {
        if (!cancelled) setState('unsupported')
        return
      }
      if (isIosNeedsInstall()) {
        if (!cancelled) setState('ios-needs-install')
        return
      }
      // Already blocked at the browser level → reflect denied up front.
      if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
        if (!cancelled) {
          setState('denied')
          setMessage(
            'Notifications are blocked in your browser settings. Enable them for this site to receive push.',
          )
        }
        return
      }
      try {
        const reg = await navigator.serviceWorker.getRegistration('/sw.js')
        const existing = reg ? await reg.pushManager.getSubscription() : null
        if (!cancelled) setState(existing ? 'subscribed' : 'idle')
      } catch (_e) {
        if (!cancelled) setState('idle')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const enable = useCallback(async () => {
    if (busy) return
    setBusy(true)
    setMessage('')
    setState('prompting')
    try {
      const sub = await subscribeToPush()
      const endpoint = sub?.endpoint
      const p256dh = sub?.keys?.p256dh
      const authKey = sub?.keys?.auth
      if (!endpoint || !p256dh || !authKey) {
        throw new Error('Subscription was missing endpoint or keys.')
      }

      // user_id is set by the column default auth.uid() and enforced by the
      // own_insert RLS with-check — do NOT send it from the client.
      const { error } = await supabase.from('push_subscriptions').upsert(
        {
          endpoint,
          p256dh,
          auth: authKey,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          restaurant_id: currentRestaurantId || null,
        },
        // Uniqueness is per-user (user_id, endpoint). user_id is NOT sent —
        // the column default auth.uid() fills it and ON CONFLICT matches on
        // the final row values, so a recycled endpoint string for THIS user
        // resolves to their own row, never another user's.
        { onConflict: 'user_id,endpoint' },
      )
      if (error) throw error

      setState('subscribed')
      setMessage('Push notifications are on for this device.')
    } catch (e) {
      if (e instanceof PushPermissionError) {
        setState('denied')
        setMessage(
          'Notifications are blocked in your browser settings. Enable them for this site to receive push.',
        )
      } else if (e instanceof PushConfigError) {
        // Missing VITE_VAPID_PUBLIC_KEY — the build is half-configured.
        // Surface a friendly nudge, NOT a raw error, and leave the device
        // un-registered (the config guard throws before any SW register or
        // permission prompt).
        setState('error')
        setMessage("Push isn't configured yet. We're on it — try again later.")
      } else {
        // eslint-disable-next-line no-console
        console.error('[usePushSubscription] enable failed', e)
        setState('error')
        setMessage("We couldn't turn on push notifications. Try again.")
      }
    } finally {
      setBusy(false)
    }
  }, [busy, currentRestaurantId])

  const disable = useCallback(async () => {
    if (busy) return
    setBusy(true)
    setMessage('')
    try {
      // Read the endpoint BEFORE tearing anything down so we can delete the
      // DB row first. Ordering matters: if the browser unsubscribe succeeded
      // but the row delete failed, we'd be left with a dead row that keeps
      // getting pushed. Deleting the row FIRST means the worst case is an
      // orphaned browser sub with no row — harmless (no send target).
      const endpoint = await getSubscriptionEndpoint()
      if (endpoint) {
        const { error } = await supabase
          .from('push_subscriptions')
          .delete()
          .eq('endpoint', endpoint)
        if (error) throw error
      }
      await unsubscribeFromPush()
      setState('idle')
      setMessage('Push notifications are off for this device.')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[usePushSubscription] disable failed', e)
      setState('error')
      setMessage("We couldn't turn off push notifications. Try again.")
    } finally {
      setBusy(false)
    }
  }, [busy])

  return { state, busy, message, enable, disable }
}
